import { Switch } from "@clockwise/design-system";
import { Size } from "@clockwise/design-system/src/components/Switch";
import { bg_positive_emphasis, fg_muted, fg_warning_muted } from "@clockwise/design-system/tokens";
import {
  CheckCircleFilled,
  ChevronRight,
  PauseCircleFilled,
  WarningFilled,
} from "@clockwise/icons";
import { FlexRange } from "@clockwise/schema/v2";
import { FlexOutlineIcon } from "@clockwise/web-commons/src/components/module-flex-settings/atoms/FlexOutlineIcon";
import { FlexSettingsModule } from "@clockwise/web-commons/src/components/module-flex-settings/FlexSettingsModule";
import { FlexibleStatusSimplified } from "@clockwise/web-commons/src/components/module-flex-settings/types";
import { DayOnOffMap } from "@clockwise/web-commons/src/ui/working-hours";
import { SvgIconTypeMap } from "@material-ui/core";
import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import { AssistantRounded } from "@material-ui/icons";
import classNames from "classnames";
import { debounce, noop } from "lodash";
import { default as React, useState } from "react";
import { InteractiveTooltip } from "../../../interactive-tooltip";
import { ListItemButton } from "../../calendar-popover/atoms/ListItemButton";

const EventFlexibilityStatusIcon = ({
  flexibleStatus,
}: {
  flexibleStatus: FlexibleStatusSimplified;
}) => {
  switch (flexibleStatus) {
    case "paused":
    case "paywall":
      return (
        <PauseCircleFilled
          fill={fg_warning_muted}
          style={{ color: fg_warning_muted }}
          className="cw-h-4 cw-w-4"
        />
      );
    case "stuck":
      return (
        <WarningFilled
          fill={fg_warning_muted}
          style={{ color: fg_warning_muted }}
          className="cw-h-4 cw-w-4"
        />
      );
    case "pinned":
    case "default":
      return (
        <CheckCircleFilled
          fill={bg_positive_emphasis}
          style={{ color: bg_positive_emphasis }}
          className="cw-h-4 cw-w-4"
        />
      );
  }
};

export const EventFlexibilityHoverCard = ({
  flexible,
  flexibleEditable,
  flexibleDayOnOffMap,
  flexibleEnd,
  flexibleRange,
  flexibleStart,
  flexibleStatus,
  onChangeFlexible,
  onChangeFlexibleDays,
  onChangeFlexibleRange,
  onChangeFlexibleResume,
  onChangeFlexibleTimeRange,
  updatingFlexible,
  attendeeBoundStart,
  attendeeBoundEnd,
  size = "small",
}: {
  flexible: boolean;
  flexibleEditable: boolean;
  flexibleDayOnOffMap: DayOnOffMap;
  flexibleEnd: string;
  flexibleRange: FlexRange;
  flexibleStart: string;
  flexibleStatus: FlexibleStatusSimplified;
  onChangeFlexible: (flexible: boolean) => void;
  onChangeFlexibleDays: (dayOnOffMap: DayOnOffMap) => void;
  onChangeFlexibleRange: (flexibleRange: FlexRange) => void;
  onChangeFlexibleResume: () => void;
  onChangeFlexibleTimeRange: (start: string, end: string) => void;
  updatingFlexible: boolean;
  attendeeBoundStart: string | null;
  attendeeBoundEnd: string | null;
  size?: "small" | "medium";
}) => {
  const [tooltipOpen, _setTooltipOpen] = useState(false);

  let switchSize: Size, icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  switch (size) {
    case "medium":
      switchSize = "small";
      icon = AssistantRounded;
      break;
    case "small":
    default:
      switchSize = "xs";
      icon = FlexOutlineIcon;
      break;
  }

  const setTooltipOpenDebounced = debounce((isOpen: boolean) => {
    _setTooltipOpen(isOpen);
  }, 150);

  const setTooltipOpen = (isOpen: boolean) => {
    if (isOpen) {
      _setTooltipOpen(isOpen);
    }

    setTooltipOpenDebounced(isOpen);
  };

  const onEnable = () => {
    if (flexible) {
      return;
    }
    const paywall = flexibleStatus === "paywall";
    const canUpdate = !paywall && !updatingFlexible && flexibleEditable;
    if (canUpdate) {
      onChangeFlexible(!flexible);
    }
  };

  return (
    <InteractiveTooltip
      open={tooltipOpen}
      title={
        flexible ? (
          <div
            cw-id="event-flexibility-portal"
            className={classNames(
              "cw-rounded-xl cw-w-[210px] cw-overflow-auto cw-body-sm cw-text-12 cw-bg-default cw-shadow-selectPopup",
              "cw-py-4 cw-px-3",
            )}
            onMouseOver={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
          >
            <FlexSettingsModule
              attendeeBoundEnd={attendeeBoundEnd}
              attendeeBoundStart={attendeeBoundStart}
              flexible={flexible}
              flexibleDayOnOffMap={flexibleDayOnOffMap}
              flexibleEditable={flexibleEditable}
              flexibleEnd={flexibleEnd}
              flexibleRange={flexibleRange}
              flexibleStart={flexibleStart}
              flexibleStatus={flexibleStatus}
              onChangeFlexible={onChangeFlexible}
              onChangeFlexibleDays={onChangeFlexibleDays}
              onChangeFlexibleRange={onChangeFlexibleRange}
              onChangeFlexibleResume={onChangeFlexibleResume}
              onChangeFlexibleTimeRange={onChangeFlexibleTimeRange}
              updating={updatingFlexible}
              noIcon
            />
          </div>
        ) : (
          ""
        )
      }
    >
      <div
        onMouseOver={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
      >
        <ListItemButton startIcon={icon} onClick={noop} disabled={!flexibleEditable}>
          <div
            className={classNames(
              "cw-flex cw-flex-row cw-items-center cw-justify-between cw-w-full",
              {
                "cw-ml-1": size === "medium",
              },
            )}
            onClick={() => onEnable()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onEnable();
              }
            }}
          >
            <div className={classNames({ "cw-font-medium": size === "medium" })}>
              {flexible ? "Event flexibility" : "Mark as flexible"}
            </div>
            <div className="cw-flex cw-items-center cw-gap-2">
              {flexible ? (
                <>
                  <EventFlexibilityStatusIcon flexibleStatus={flexibleStatus} />
                  <ChevronRight
                    fill={fg_muted}
                    style={{ color: fg_muted }}
                    className="cw-h-4 cw-w-4"
                  />
                </>
              ) : (
                <Switch
                  checked={flexible}
                  disabled={updatingFlexible || !flexibleEditable}
                  sentiment="neutral"
                  size={switchSize}
                />
              )}
            </div>
          </div>
        </ListItemButton>
      </div>
    </InteractiveTooltip>
  );
};
